import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowRight, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
    const { user, login, logout } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user && (user.role === "admin" || user.role === "super_admin")) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const u = await login(email, password);
            if (u.role !== "admin" && u.role !== "super_admin") {
                await logout();
                throw new Error("This account does not have administrator access. Please use the user sign-in.");
            }
            navigate("/admin");
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-white grid grid-cols-1 lg:grid-cols-12 relative overflow-hidden">
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />
            <div className="lg:col-span-7 flex flex-col p-8 sm:p-12 lg:p-16 relative z-10">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5 group" data-testid="admin-logo-link">
                        <div className="h-8 w-8 bg-white text-slate-950 grid place-items-center font-heading font-bold tracking-tight rounded-sm">A</div>
                        <div className="leading-tight">
                            <div className="font-heading font-bold text-white text-base">Allied Pod</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/50 font-mono">Admin Portal</div>
                        </div>
                    </Link>
                    <Link to="/login" data-testid="admin-back-to-user-link">
                        <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10">
                            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.75} /> User sign-in
                        </Button>
                    </Link>
                </div>
                <div className="flex-1 flex flex-col justify-center max-w-md w-full">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/10 bg-white/5 w-fit">
                        <ShieldCheck className="h-3.5 w-3.5 text-amber-400" strokeWidth={1.75} />
                        <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-white/70">Restricted access</span>
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold mt-5 leading-tight">
                        Administrator<br/>sign in.
                    </h1>
                    <p className="text-sm text-white/60 mt-3 leading-relaxed">
                        For Admin and Super Admin accounts only. Regular users should sign in via the public page.
                    </p>
                    <form onSubmit={submit} className="mt-10 space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-[0.16em] font-mono text-white/60">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                required
                                data-testid="admin-login-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5 h-11 bg-white/5 border-white/10 rounded-md text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-amber-400 focus-visible:border-amber-400"
                                placeholder="admin@alliedconsulting.global"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-[0.16em] font-mono text-white/60">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                required
                                data-testid="admin-login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 h-11 bg-white/5 border-white/10 rounded-md text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-amber-400 focus-visible:border-amber-400"
                                placeholder="••••••••"
                            />
                        </div>
                        {err && (
                            <div data-testid="admin-login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-sm">
                                {err}
                            </div>
                        )}
                        <Button
                            type="submit"
                            data-testid="admin-login-submit-btn"
                            disabled={loading}
                            className="w-full h-11 bg-amber-400 hover:bg-amber-300 text-slate-950 font-medium rounded-md group"
                        >
                            {loading ? "Signing in…" : (
                                <>
                                    Sign in to portal
                                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                                </>
                            )}
                        </Button>
                        <div className="text-center">
                            <Link to="/forgot-password" className="text-xs text-white/50 hover:text-amber-400 transition-colors underline-offset-4 hover:underline">
                                Forgot your password?
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
            <div className="hidden lg:block lg:col-span-5 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-transparent to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-amber-400 mb-3">Admin scope</p>
                    <ul className="space-y-2 text-sm text-white/80">
                        <li>· View & manage all bookings</li>
                        <li>· Create / edit / delete user accounts</li>
                        <li>· Issue and revoke credits</li>
                        <li>· Override system limits</li>
                        <li>· Block dates &amp; manage public holidays</li>
                        <li>· Verify PayPal payments</li>
                        <li>· Access activity logs &amp; analytics</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}
